import React, { useEffect, useRef, useMemo, useState } from 'react';
import lottoGlassImg from '../../assets/CanvasImages/lottery-machine.png';
import lottoPipe from '../../assets/CanvasImages/finalPathpng.png';
import baseMachine from '../../assets/CanvasImages/baseMachine.png';
import ballSetImage from '../../assets/CanvasImages/secondTest.png';
import { useTheme } from '../../utils/ThemeContext';
import { useDispatch, useSelector } from 'react-redux';
import lotterySound from '../../assets/sounds/lotteryLatest.mp3';
import { resetResults } from '../../store/gameSlice';

const LottoCanvas = ({ sound, isUserInteracted }) => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const audioRef = useRef(new Audio(lotterySound));
  const dispatch = useDispatch();

  const resultPick3 = useSelector((state) => state.game.resultPick3);
  const resultLotto_5_90 = useSelector((state) => state.game.resultLotto_5_90);
  const resultLotto_4_48 = useSelector((state) => state.game.resultLotto_4_48);
  const resultLotto_6_49 = useSelector((state) => state.game.resultLotto_6_49);

  const resultNumbers = useMemo(() => {
    if (theme.themeName === 'pick 3') return resultPick3;
    if (theme.themeName === 'lotto 5/90') return resultLotto_5_90;
    if (theme.themeName === 'lotto 4/48') return resultLotto_4_48;
    if (theme.themeName === 'Lotto 6/49') return resultLotto_6_49;
    return [];
  }, [
    theme.themeName,
    resultPick3,
    resultLotto_5_90,
    resultLotto_4_48,
    resultLotto_6_49,
  ]);

  useEffect(() => {
    dispatch(resetResults());
  }, [theme.themeName, dispatch]);

  useEffect(() => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  }, [theme.themeName]);

  const spriteWidth = 1500;
  const spriteHeight = 158;
  const numberOfBalls = 10;
  const ballWidth = spriteWidth / numberOfBalls;
  const ballHeight = spriteHeight;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = 700;
    canvas.height = 700;
    const verticalOffset = -80;

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    };

    Promise.all([
      loadImage(lottoGlassImg),
      loadImage(lottoPipe),
      loadImage(baseMachine),
      loadImage(ballSetImage),
    ])
      .then(([lottoGlass, lottoPipe, baseMachine, ballSetImage]) => {
        class CircleMachine {
          constructor({ x, y, radius, image }) {
            this.center = { x, y };
            this.radius = radius;
            this.image = image;
            this.width = 315;
            this.height = 315;
          }
          draw() {
            ctx.drawImage(
              this.image,
              this.center.x - this.width / 1.93,
              this.center.y - this.height / 2,
              this.width,
              this.height,
            );
          }
        }

        class BaseMachine {
          constructor({ x, y, image }) {
            this.center = { x, y };
            this.image = image;
            this.width = 180;
            this.height = 50;
          }
          draw() {
            ctx.drawImage(
              this.image,
              this.center.x - this.width / 2,
              this.center.y - this.height / 1.2,
              this.width,
              this.height,
            );
          }
        }

        class Pipe {
          constructor({ x, y, image }) {
            this.center = { x, y };
            this.image = image;
            this.width = 520;
            this.height = 530;
          }
          draw() {
            ctx.drawImage(
              this.image,
              this.center.x - this.width / 1.67,
              this.center.y - this.height / 2,
              this.width,
              this.height,
            );
          }
        }

        class Ball {
          constructor({ x, y, radius, number, velocity }) {
            this.position = { x, y };
            this.radius = radius;
            this.number = number;
            this.velocity = velocity;
            this.isHidden = false;
            this.isHiding = false;
            this.reachedPipeCurve = false;
            this.reachedCenter = false;
            this.pipeAngle = -Math.PI / 2;
            this.movedUp = false;
            this.rotation = 0;
            this.fontSize = 15;
          }

          draw() {
            if (!this.isHidden) {
              const spriteX = (this.number % numberOfBalls) * ballWidth;
              const spriteY = 0;

              ctx.save();
              ctx.translate(this.position.x, this.position.y);
              ctx.rotate(this.rotation);

              ctx.drawImage(
                ballSetImage,
                spriteX,
                spriteY,
                ballWidth,
                ballHeight,
                -this.radius,
                -this.radius,
                this.radius * 2,
                this.radius * 2,
              );

              ctx.font = `${this.fontSize}px Arial`;
              ctx.fillStyle = 'black';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(this.number, 0, 0);

              ctx.restore();
            }
          }
          update(circleMachine, pipe) {
            if (this.isHiding) {
              this.animateHide(circleMachine, pipe);
            } else if (!this.isHidden) {
              this.position.x += this.velocity.x;
              this.position.y += this.velocity.y;

              const dx = this.position.x - circleMachine.center.x;
              const dy = this.position.y - circleMachine.center.y;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance + this.radius > circleMachine.radius) {
                const normalX = dx / distance;
                const normalY = dy / distance;
                const dotProduct =
                  this.velocity.x * normalX + this.velocity.y * normalY;
                this.velocity.x -= 2 * dotProduct * normalX;
                this.velocity.y -= 2 * dotProduct * normalY;

                const overlap = distance + this.radius - circleMachine.radius;
                this.position.x -= overlap * normalX;
                this.position.y -= overlap * normalY;
              }

              // Damping to slow down over time
              const damping = 0.99999;
              this.velocity.x *= damping;
              this.velocity.y *= damping;

              // Add slight randomness to simulate jitter or natural movement
              this.velocity.x += (Math.random() - 0.5) * 0.1;
              this.velocity.y += (Math.random() - 0.5) * 0.1;

              // Cap the maximum speed
              const maxSpeed = 5.5;
              const speed = Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2);
              if (speed > maxSpeed) {
                this.velocity.x = (this.velocity.x / speed) * maxSpeed;
                this.velocity.y = (this.velocity.y / speed) * maxSpeed;
              }

              this.draw();
            }
          }

          animateHide(circleMachine) {
            const centerX = circleMachine.center.x;
            const centerY = circleMachine.center.y;
            const pipeCurveRadius = circleMachine.radius + 30;
            const pipeStartY = centerY - pipeCurveRadius;
            const pipeExitX = centerX - pipeCurveRadius - 90;
            const pipeExitY = canvas.height - 100;
            const speed = 3;

            if (!this.reachedCenter) {
              const dx = centerX - this.position.x;
              const dy = centerY - this.position.y;
              this.radius = 28;
              this.fontSize = 20;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance > 7) {
                this.position.x += (dx / distance) * speed;
                this.position.y += (dy / distance) * speed;
              } else {
                this.reachedCenter = true;
              }
            } else if (!this.movedUp) {
              if (this.position.y > pipeStartY) {
                this.position.y -= speed;
              } else {
                this.movedUp = true;
                this.pipeAngle = -Math.PI / 2;
              }
            } else if (!this.reachedPipeCurve) {
              if (this.pipeAngle < Math.PI / 2) {
                this.pipeAngle += 0.02;
                this.position.x =
                  centerX + Math.cos(this.pipeAngle) * pipeCurveRadius;
                this.position.y =
                  centerY + Math.sin(this.pipeAngle) * pipeCurveRadius;
                this.rotation += -0.1;
              } else {
                this.reachedPipeCurve = true;
              }
            } else {
              const hiddenIndex = hiddenBalls.indexOf(this);
              const offset = hiddenIndex * (this.radius * 2);
              const targetX = pipeExitX + offset;
              const targetY = pipeExitY;
              const dx = targetX - this.position.x;
              const dy = targetY - this.position.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance > 1) {
                this.position.x += (dx / distance) * speed;

                if (Math.abs(dx) > 9) {
                  this.rotation += -0.1;
                } else {
                  this.rotation = 0;
                }
              } else {
                this.isHidden = true;
                this.rotation = -0;
                return;
              }
            }

            this.draw();
          }
        }

        const circleMachine = new CircleMachine({
          x: canvas.width / 2,
          y: canvas.height / 2 + verticalOffset,
          radius: 140,
          image: lottoGlass,
        });

        const basedMachine = new BaseMachine({
          x: canvas.width / 2,
          y: canvas.height / 1.4 + verticalOffset + 35,
          image: baseMachine,
        });

        const pipe = new Pipe({
          x: canvas.width / 2,
          y: canvas.height / 2 + verticalOffset,
          image: lottoPipe,
        });

        const balls = [];
        const ballRadius = 15;
        const hiddenBalls = [];
        let ballNumbers = [];

        if (theme.themeName === 'pick 3') {
          for (let i = 0; i < 3; i++) {
            ballNumbers.push(
              ...Array.from({ length: 10 }, (_, index) => index),
            );
          }
        } else {
          ballNumbers = Array.from(
            { length: theme.selectedNumbers },
            (_, i) => i + 1,
          );
        }

        for (let i = 0; i < theme.selectedNumbers; i++) {
          const angle = Math.random() * Math.PI * 2;
          const distance =
            Math.random() * (circleMachine.radius - ballRadius - 5);
          const x = circleMachine.center.x + Math.cos(angle) * distance;
          const y = circleMachine.center.y + Math.sin(angle) * distance;
          const velocity = { x: 2.5, y: 2.5 };

          balls.push(
            new Ball({
              x,
              y,
              radius: ballRadius,
              number: ballNumbers[i],
              velocity,
            }),
          );
        }

        function startAutoHide() {
          if (resultNumbers && resultNumbers.length > 0) {
            let currentIndex = 0;

            if (isUserInteracted && sound === true) {
              audioRef.current.play().catch((err) => {
                console.error('Audio playback failed:', err);
              });
            }

            const interval = setInterval(() => {
              if (currentIndex < resultNumbers.length) {
                const number = resultNumbers[currentIndex];
                hideBallByNumber(number);
                currentIndex++;
              } else {
                clearInterval(interval);
                setTimeout(() => {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                }, 2000);
              }
            }, 2000);
          }
        }

        function hideBallByNumber(number) {
          for (let ball of balls) {
            if (ball.number === number && !ball.isHidden && !ball.isHiding) {
              ball.isHiding = true;
              hiddenBalls.push(ball);
              break;
            }
          }
        }

        startAutoHide();

        function animate() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          circleMachine.draw();
          basedMachine.draw();
          pipe.draw();
          balls.forEach((ball) => ball.update(circleMachine, pipe));
          requestAnimationFrame(animate);
        }

        animate();
      })
      .catch((error) => {
        console.error('Error loading images:', error);
      });
  }, [resultNumbers, theme.themeName]);

  return <canvas ref={canvasRef} width={500} height={500}></canvas>;
};

export default LottoCanvas;
